import CalendarIcon from '@mui/icons-material/InsertInvitation';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import {
  NextIconButton,
  PreviousIconButton,
  SwitchViewButton,
} from '@openx/components-core/lib/CalendarNavigationButtons/CalendarNavigationButtons';
import { DATE_FORMAT } from '@openx/types/segments';
import { format } from 'date-fns';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createDateRangeString } from './utils';

type DatePickerFilterProps = {
  minDate?: Date;
  maxDate?: Date;
  onDateChange: (date: string | null) => void;
};

export const DatePickerFilter = ({ minDate, maxDate, onDateChange }: DatePickerFilterProps) => {
  const [dateArray, setDateArray] = useState<Array<string | null>>([]);
  const [startDate, endDate] = dateArray;
  const { t } = useTranslation();

  const handleStartDateChange = useCallback(
    (date: Date | null) => {
      const isValid = date instanceof Date && !Number.isNaN(date.getTime());

      setDateArray(prev => {
        const updatedDate = [isValid ? format(date, DATE_FORMAT) : null, prev[1]];
        const dateString = createDateRangeString(updatedDate, t);

        onDateChange(dateString);

        return updatedDate;
      });
    },
    [onDateChange, t]
  );

  const handleEndDateChange = useCallback(
    (date: Date | null) => {
      const isValid = date instanceof Date && !Number.isNaN(date.getTime());

      setDateArray(prev => {
        const updatedDate = [prev[0], isValid ? format(date, DATE_FORMAT) : null];
        const dateString = createDateRangeString(updatedDate, t);

        onDateChange(dateString);

        return updatedDate;
      });
    },
    [onDateChange, t]
  );

  const endDateFormatted = endDate ? new Date(endDate) : maxDate;
  const startDateFormatted = startDate ? new Date(startDate) : minDate;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Grid container spacing={1}>
        <Grid size={6} data-test="start-date">
          <Typography
            variant="body2"
            sx={{
              marginLeft: 1,
            }}
          >
            {t('From')}
          </Typography>
          <DatePicker
            disableFuture
            minDate={minDate}
            maxDate={endDateFormatted}
            onChange={handleStartDateChange}
            slots={{
              openPickerIcon: CalendarIcon,
              previousIconButton: PreviousIconButton,
              nextIconButton: NextIconButton,
              switchViewButton: SwitchViewButton,
            }}
            slotProps={{
              openPickerIcon: {
                // @ts-expect-error there is no type definition for data-* attributes on this slot, although it works
                'data-test': 'calendar-icon',
              },
            }}
          />
        </Grid>

        <Grid size={6} data-test="end-date">
          <Typography
            variant="body2"
            sx={{
              marginLeft: 1,
            }}
          >
            {t('To')}
          </Typography>
          <DatePicker
            disableFuture
            minDate={startDateFormatted}
            maxDate={maxDate}
            onChange={handleEndDateChange}
            slots={{
              openPickerIcon: CalendarIcon,
              previousIconButton: PreviousIconButton,
              nextIconButton: NextIconButton,
              switchViewButton: SwitchViewButton,
            }}
            slotProps={{
              openPickerIcon: {
                // @ts-expect-error there is no type definition for data-* attributes on this slot, although it works
                'data-test': 'calendar-icon',
              },
            }}
          />
        </Grid>
      </Grid>
    </LocalizationProvider>
  );
};
